import { useService } from "@policoders/di";
import { TColumn, TTableActions, TTableLineActions } from "@policoders/ds";
import { useEffect, useState } from "react";

import formatHelper from "../../../../../core/helpers/formatHelper";
import TechnicalSheetItemForm from "../form-item/technical-sheet-item.form";
import { TechnicalSheet, TechnicalSheetItem } from "../technical-sheet.model";
import { TechnicalSheetStore } from "../technical-sheet.store";

export type ITechnicalSheetFormProps = {
  data: TechnicalSheet;
  onChange: (data: TechnicalSheet) => void;
};

export const useTechnicalSheetForm = ({ data, onChange }: ITechnicalSheetFormProps) => {
  const [state, setState] = useState<TechnicalSheet>(data);
  const [lines, setLines] = useState<TechnicalSheetItem[]>([]);
  const [estimativas, setEstimativas] = useState<any>({});
  const [margemLucro, setMargemLucro] = useState<any>({});
  const store = useService<TechnicalSheetStore>("TechnicalSheetStore");

  useEffect(() => {
    store.load();
    var subscriber = store.current$.subscribe(current => {
      setLines([
        ...current.items
          .map(item => {
            const product = store.getProduct(item.productId);
            return {
              ...item,
              productName: product?.productName,
              unitFormated: `${formatHelper.measure(product?.unit)} ${product?.measureUnit}`,
              grossCost: store.calcGrossCost(product, item),
              grossCostFormated: formatHelper.currency(store.calcGrossCost(product, item)),
              recipeQuantityFormated: `${formatHelper.measure(item.recipeQuantity)} ${
                item.measureUnit
              }`,
            };
          })
          .sort((a, b) => (a.productName || "").localeCompare(b.productName || "")),
      ]);
    });
    return () => {
      subscriber.unsubscribe();
    };
  }, [store]);

  useEffect(() => {
    const totais = lines.reduce(
      (p, c: any) => {
        return {
          pesoTotalKg: c.measureUnit === "kg" ? p.pesoTotal + c.recipeQuantity : p.pesoTotal,
          pesoTotalL: c.measureUnit === "kg" ? p.pesoTotal + c.recipeQuantity : p.pesoTotal,
          custoTotal: p.custoTotal + c.grossCost,
        };
      },
      { pesoTotal: 0, custoTotal: 0 } as any,
    );
    setEstimativas({
      pesoTotal: formatHelper.measure(totais.pesoTotal),
      custoTotal: formatHelper.currency(totais.custoTotal),
      custoPorcao: formatHelper.currency((totais.custoTotal || 0) / (state.portions || 1)),
      pesoPorcao: formatHelper.measure((totais.pesoTotal || 0) / (state.portions || 1)),
      percentualReal:
        state.salePrice || state.portionCost
          ? Math.round(100 * ((state.salePrice || 0) / (state.portionCost || 1) - 1))
          : 0,
    });
    setMargemLucro({
      p15: {
        unit: formatHelper.currency((totais.custoTotal / state.portions || 1) * 1.15),
        total: formatHelper.currency(totais.custoTotal * 1.15),
      },
      p30: {
        unit: formatHelper.currency((totais.custoTotal / state.portions || 1) * 1.3),
        total: formatHelper.currency(totais.custoTotal * 1.3),
      },
      p40: {
        unit: formatHelper.currency((totais.custoTotal / state.portions || 1) * 1.4),
        total: formatHelper.currency(totais.custoTotal * 1.4),
      },
      p50: {
        unit: formatHelper.currency((totais.custoTotal / state.portions || 1) * 1.5),
        total: formatHelper.currency(totais.custoTotal * 1.5),
      },
      p100: {
        unit: formatHelper.currency((totais.custoTotal / state.portions || 1) * 2),
        total: formatHelper.currency(totais.custoTotal * 2),
      },
      p150: {
        unit: formatHelper.currency((totais.custoTotal / state.portions || 1) * 2.5),
        total: formatHelper.currency(totais.custoTotal * 2.5),
      },
      p200: {
        unit: formatHelper.currency((totais.custoTotal / state.portions || 1) * 3),
        total: formatHelper.currency(totais.custoTotal * 3),
      },
      p300: {
        unit: formatHelper.currency((totais.custoTotal / state.portions || 1) * 4),
        total: formatHelper.currency(totais.custoTotal * 3),
      },
    });
  }, [lines, state.portionCost, state.portions, state.salePrice, store]);

  useEffect(() => {
    onChange && onChange(state);
  }, [onChange, state]);

  const handleChange = (e: any) => {
    setState({ ...state, [e.target.name]: e.target.value });
  };

  const handleSaveItem = () => {
    try {
      const model = store.currentItem;
      const entity = TechnicalSheetItem.build(model);
      setState({
        ...state,
        items: [...state.items.filter(x => x.productId !== entity.productId), entity],
      });
      store.ui.subModal.close();
    } catch (e: any) {
      store.showValidationMessage(e.message);
    }
  };

  const handleNew = () => {
    const item = new TechnicalSheetItem();
    store.updateCurrentItem(item);
    store.ui.subModal
      .config({
        size: "large",
        title: "adicionar item",
        buttonOkLabel: "Ok",
        buttonOkAction: handleSaveItem,
        children: () => <TechnicalSheetItemForm data={item} action={"new"} />,
      })
      .open();
  };

  const handleEdit = (line: TechnicalSheetItem) => {
    const item = lines.find(x => x.productId === line.productId);
    if (!item) return;
    store.updateCurrentItem(item);
    store.ui.subModal
      .config({
        size: "large",
        title: `editar item (${item.productId.split("-")[0]})`,
        buttonOkLabel: "Ok",
        buttonOkAction: handleSaveItem,
        children: () => <TechnicalSheetItemForm data={item} action={"edit"} />,
      })
      .open();
  };

  const handleDelete = (line: TechnicalSheetItem) => {
    if (window.confirm("excluir item?")) {
      setState({
        ...state,
        items: state.items.filter(x => x.productId !== line.productId),
      });
    }
  };

  const tActions: TTableLineActions[] = [{ label: "excluir", onClick: handleDelete }];

  const tColumns: TColumn[] = [
    { field: "productName", title: "Produto", columnClass: "text-nowrap" },
    { field: "unitFormated", title: "Qtd. compra", columnClass: "d-lg-table-cell text-nowrap" },
    {
      field: "recipeQuantityFormated",
      title: "Qtd. na receita",
      columnClass: "d-sm-table-cell text-nowrap",
    },
    {
      field: "grossCostFormated",
      title: "Custo bruto",
      columnClass: "d-lg-table-cell text-nowrap",
    },
  ];

  const tHeaderButtons: TTableActions = {
    buttonNew: {
      label: "Novo",
      action: handleNew,
    },
  };

  return {
    lines,
    tActions,
    tColumns,
    tHeaderButtons,
    state,
    estimativas,
    margemLucro,
    handleChange,
    handleEdit,
  };
};
